// ===========================================================================
// Crowd/Mob Detection Pipeline
// Implements grouping, heatmap, morphology, contours, and alert logic
// ===========================================================================

import {
  Track,
  PipelineThresholds,
  Gathering,
  CrowdRegion,
  GRID_COLS,
  GRID_ROWS,
  WORLD_WIDTH,
  WORLD_HEIGHT,
} from '../types';
import { sampleDepthBilinear } from './depth';

// ----------------------------- Grouping ------------------------------------

/**
 * Find connected components of people based on spatial and depth proximity
 */
export function findGatherings(
  tracks: Track[],
  depthGrid: number[][],
  thresholds: PipelineThresholds
): Gathering[] {
  const confirmedTracks = tracks.filter((t) => t.confirmed);
  if (confirmedTracks.length === 0) return [];

  // Get depths for all tracks
  const trackDepths = confirmedTracks.map((t) =>
    sampleDepthBilinear(depthGrid, t.cx, t.cy)
  );

  // Build adjacency list using Union-Find
  const parent = confirmedTracks.map((_, i) => i);
  const rank = confirmedTracks.map(() => 0);

  function find(i: number): number {
    if (parent[i] !== i) {
      parent[i] = find(parent[i]);
    }
    return parent[i];
  }

  function union(i: number, j: number): void {
    const pi = find(i);
    const pj = find(j);
    if (pi === pj) return;

    if (rank[pi] < rank[pj]) {
      parent[pi] = pj;
    } else if (rank[pi] > rank[pj]) {
      parent[pj] = pi;
    } else {
      parent[pj] = pi;
      rank[pi]++;
    }
  }

  // Check all pairs
  for (let i = 0; i < confirmedTracks.length; i++) {
    for (let j = i + 1; j < confirmedTracks.length; j++) {
      const ti = confirmedTracks[i];
      const tj = confirmedTracks[j];

      // Spatial distance
      const dx = ti.cx - tj.cx;
      const dy = ti.cy - tj.cy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      // Depth difference
      const depthDiff = Math.abs(trackDepths[i] - trackDepths[j]);

      if (
        dist <= thresholds.spatialDistanceThreshold &&
        depthDiff <= thresholds.depthThreshold
      ) {
        union(i, j);
      }
    }
  }

  // Group by component
  const components = new Map<number, number[]>();
  for (let i = 0; i < confirmedTracks.length; i++) {
    const root = find(i);
    if (!components.has(root)) {
      components.set(root, []);
    }
    components.get(root)!.push(i);
  }

  // Create gatherings from components >= minGroupSize
  const gatherings: Gathering[] = [];
  let gatheringId = 0;

  for (const [, members] of components) {
    if (members.length < thresholds.minGroupSize) continue;

    // Compute centroid and average depth
    let sumX = 0;
    let sumY = 0;
    let sumDepth = 0;

    for (const idx of members) {
      sumX += confirmedTracks[idx].cx;
      sumY += confirmedTracks[idx].cy;
      sumDepth += trackDepths[idx];
    }

    const centroid = {
      x: sumX / members.length,
      y: sumY / members.length,
    };
    const avgDepth = sumDepth / members.length;

    gatherings.push({
      id: `gathering_${gatheringId++}`,
      memberTrackIds: members.map((i) => confirmedTracks[i].trackId),
      centroid,
      depth: avgDepth,
      size: members.length,
    });
  }

  return gatherings;
}

// ----------------------------- Heatmap -------------------------------------

/**
 * Build heatmap grid from gatherings
 * Each gathering adds a gaussian impulse weighted by inverse depth
 */
export function buildHeatmapGrid(
  gatherings: Gathering[],
  sigma: number,
  historyBuffer: number[][][],
  historyLength: number
): number[][] {
  const grid = createEmptyGrid();

  // Add impulses for each gathering
  for (const g of gatherings) {
    const weight = 1 / (g.depth + 0.1); // Inverse depth weighting
    addGaussianImpulse(grid, g.centroid.x, g.centroid.y, sigma, weight * g.size);
  }

  // Add to history buffer
  historyBuffer.push(grid);
  while (historyBuffer.length > historyLength) {
    historyBuffer.shift();
  }

  // Average with history (temporal smoothing)
  if (historyBuffer.length > 1) {
    const smoothed = createEmptyGrid();
    for (let row = 0; row < GRID_ROWS; row++) {
      for (let col = 0; col < GRID_COLS; col++) {
        let sum = 0;
        for (const h of historyBuffer) {
          sum += h[row][col];
        }
        smoothed[row][col] = sum / historyBuffer.length;
      }
    }
    return smoothed;
  }

  return grid;
}

/**
 * Add a gaussian impulse to the grid at world position
 */
function addGaussianImpulse(
  grid: number[][],
  worldX: number,
  worldY: number,
  sigma: number,
  weight: number
): void {
  const gcx = (worldX / WORLD_WIDTH) * GRID_COLS;
  const gcy = (worldY / WORLD_HEIGHT) * GRID_ROWS;

  const radius = Math.ceil(sigma * 3);

  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const gx = Math.round(gcx + dx);
      const gy = Math.round(gcy + dy);

      if (gx < 0 || gx >= GRID_COLS || gy < 0 || gy >= GRID_ROWS) continue;

      const dist2 = dx * dx + dy * dy;
      const value = weight * Math.exp(-dist2 / (2 * sigma * sigma));
      grid[gy][gx] += value;
    }
  }
}

/**
 * Create empty grid
 */
function createEmptyGrid(): number[][] {
  return Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => 0)
  );
}

// ----------------------------- Morphology ----------------------------------

/**
 * Threshold heatmap to binary mask
 */
export function thresholdHeatmap(
  heatmap: number[][],
  threshold: number
): boolean[][] {
  // Find max value for normalization
  let maxVal = 0;
  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      maxVal = Math.max(maxVal, heatmap[row][col]);
    }
  }

  const mask: boolean[][] = Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => false)
  );

  if (maxVal <= 0) return mask;

  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      mask[row][col] = heatmap[row][col] / maxVal >= threshold;
    }
  }

  return mask;
}

/**
 * Apply morphological closing (dilation then erosion)
 */
export function morphClose(mask: boolean[][], kernelSize: number): boolean[][] {
  const dilated = dilate(mask, kernelSize);
  return erode(dilated, kernelSize);
}

/**
 * Dilation operation
 */
function dilate(mask: boolean[][], kernelSize: number): boolean[][] {
  const result: boolean[][] = Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => false)
  );

  const k = Math.floor(kernelSize / 2);

  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      let any = false;
      for (let dy = -k; dy <= k && !any; dy++) {
        for (let dx = -k; dx <= k && !any; dx++) {
          const ny = row + dy;
          const nx = col + dx;
          if (ny >= 0 && ny < GRID_ROWS && nx >= 0 && nx < GRID_COLS) {
            if (mask[ny][nx]) any = true;
          }
        }
      }
      result[row][col] = any;
    }
  }

  return result;
}

/**
 * Erosion operation
 */
function erode(mask: boolean[][], kernelSize: number): boolean[][] {
  const result: boolean[][] = Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => false)
  );

  const k = Math.floor(kernelSize / 2);

  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      let all = true;
      for (let dy = -k; dy <= k && all; dy++) {
        for (let dx = -k; dx <= k && all; dx++) {
          const ny = row + dy;
          const nx = col + dx;
          if (ny >= 0 && ny < GRID_ROWS && nx >= 0 && nx < GRID_COLS) {
            if (!mask[ny][nx]) all = false;
          } else {
            all = false;
          }
        }
      }
      result[row][col] = all;
    }
  }

  return result;
}

// ----------------------------- Contours ------------------------------------

/**
 * Find contours in binary mask using connected components and boundary tracing
 */
export function findContours(mask: boolean[][]): Array<Array<[number, number]>> {
  // Find connected components
  const labels: number[][] = Array.from({ length: GRID_ROWS }, () =>
    Array.from({ length: GRID_COLS }, () => 0)
  );

  let nextLabel = 1;
  const components: Map<number, Array<[number, number]>> = new Map();

  for (let row = 0; row < GRID_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      if (mask[row][col] && labels[row][col] === 0) {
        // BFS to find component
        const component: Array<[number, number]> = [];
        const queue: Array<[number, number]> = [[row, col]];
        labels[row][col] = nextLabel;

        while (queue.length > 0) {
          const [r, c] = queue.shift()!;
          component.push([r, c]);

          // 4-connectivity
          const neighbors: Array<[number, number]> = [
            [r - 1, c],
            [r + 1, c],
            [r, c - 1],
            [r, c + 1],
          ];

          for (const [nr, nc] of neighbors) {
            if (
              nr >= 0 &&
              nr < GRID_ROWS &&
              nc >= 0 &&
              nc < GRID_COLS &&
              mask[nr][nc] &&
              labels[nr][nc] === 0
            ) {
              labels[nr][nc] = nextLabel;
              queue.push([nr, nc]);
            }
          }
        }

        components.set(nextLabel, component);
        nextLabel++;
      }
    }
  }

  // Extract boundary for each component
  const contours: Array<Array<[number, number]>> = [];

  for (const [, pixels] of components) {
    const boundary: Array<[number, number]> = [];

    for (const [row, col] of pixels) {
      // Check if this pixel is on boundary
      const isBoundary =
        row === 0 ||
        row === GRID_ROWS - 1 ||
        col === 0 ||
        col === GRID_COLS - 1 ||
        !mask[row - 1]?.[col] ||
        !mask[row + 1]?.[col] ||
        !mask[row]?.[col - 1] ||
        !mask[row]?.[col + 1];

      if (isBoundary) {
        // Convert to world coordinates
        const worldX = ((col + 0.5) / GRID_COLS) * WORLD_WIDTH;
        const worldY = ((row + 0.5) / GRID_ROWS) * WORLD_HEIGHT;
        boundary.push([worldX, worldY]);
      }
    }

    if (boundary.length > 0) {
      // Sort boundary points for better rendering (approximate convex hull order)
      const sorted = sortBoundaryPoints(boundary);
      contours.push(sorted);
    }
  }

  return contours;
}

/**
 * Sort boundary points for smooth contour rendering
 */
function sortBoundaryPoints(
  points: Array<[number, number]>
): Array<[number, number]> {
  if (points.length < 3) return points;

  // Find centroid
  let cx = 0;
  let cy = 0;
  for (const [x, y] of points) {
    cx += x;
    cy += y;
  }
  cx /= points.length;
  cy /= points.length;

  // Sort by angle from centroid
  return points.sort((a, b) => {
    const angleA = Math.atan2(a[1] - cy, a[0] - cx);
    const angleB = Math.atan2(b[1] - cy, b[0] - cx);
    return angleA - angleB;
  });
}

/**
 * Compute area of a contour (shoelace formula)
 */
export function computeContourArea(contour: Array<[number, number]>): number {
  if (contour.length < 3) return 0;

  let area = 0;
  for (let i = 0; i < contour.length; i++) {
    const j = (i + 1) % contour.length;
    area += contour[i][0] * contour[j][1];
    area -= contour[j][0] * contour[i][1];
  }

  return Math.abs(area) / 2;
}

/**
 * Check if a point is inside a contour (ray casting)
 */
export function isPointInContour(
  x: number,
  y: number,
  contour: Array<[number, number]>
): boolean {
  let inside = false;
  const n = contour.length;

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = contour[i];
    const [xj, yj] = contour[j];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}

// ----------------------------- Crowd Regions -------------------------------

/**
 * Build crowd regions from contours
 */
export function buildCrowdRegions(
  contours: Array<Array<[number, number]>>,
  gatherings: Gathering[],
  weaponPositions: Array<{ x: number; y: number }>,
  thresholds: PipelineThresholds
): CrowdRegion[] {
  const regions: CrowdRegion[] = [];

  for (let i = 0; i < contours.length; i++) {
    const contour = contours[i];
    const area = computeContourArea(contour);

    // Count gatherings inside
    let insideCount = 0;
    for (const g of gatherings) {
      if (isPointInContour(g.centroid.x, g.centroid.y, contour)) {
        insideCount += g.size;
      }
    }

    // Check for weapon in region
    const weaponInRegion = weaponPositions.some((w) =>
      isPointInContour(w.x, w.y, contour)
    );

    // Determine if mob
    const isCrowd = insideCount >= thresholds.crowdThreshold;
    const isMob = isCrowd && (weaponInRegion || weaponPositions.length > 0);

    // Compute centroid
    let cx = 0;
    let cy = 0;
    for (const [x, y] of contour) {
      cx += x;
      cy += y;
    }
    cx /= contour.length;
    cy /= contour.length;

    regions.push({
      id: `region_${i}`,
      contour,
      area,
      insideCount,
      mob: isMob,
      weaponInRegion,
      centroid: { x: cx, y: cy },
    });
  }

  return regions;
}

// ----------------------------- Full Pipeline -------------------------------

export interface CrowdPipelineResult {
  gatherings: Gathering[];
  heatmapGrid: number[][];
  binaryMask: boolean[][];
  contours: Array<Array<[number, number]>>;
  crowdRegions: CrowdRegion[];
  flags: {
    crowd: boolean;
    mob: boolean;
    weaponGlobal: boolean;
  };
  reasoning: string[];
}

/**
 * Run the complete crowd detection pipeline
 */
export function runCrowdPipeline(
  tracks: Track[],
  depthGrid: number[][],
  weaponPositions: Array<{ x: number; y: number }>,
  thresholds: PipelineThresholds,
  heatmapHistoryBuffer: number[][][]
): CrowdPipelineResult {
  const reasoning: string[] = [];

  // Step 1: Find gatherings
  const gatherings = findGatherings(tracks, depthGrid, thresholds);
  reasoning.push(`Found ${gatherings.length} gathering(s) from ${tracks.filter(t => t.confirmed).length} confirmed tracks`);

  for (const g of gatherings) {
    reasoning.push(`  → Gathering ${g.id}: ${g.size} people at depth ${g.depth.toFixed(2)}`);
  }

  // Step 2: Build heatmap
  const heatmapGrid = buildHeatmapGrid(
    gatherings,
    thresholds.heatmapSigma,
    heatmapHistoryBuffer,
    thresholds.historyLength
  );

  // Step 3: Threshold to binary mask
  const binaryMask = thresholdHeatmap(heatmapGrid, thresholds.heatmapThreshold);

  // Step 4: Morphological closing
  const closedMask = morphClose(binaryMask, thresholds.morphKernelSize);

  // Step 5: Find contours
  const contours = findContours(closedMask);
  reasoning.push(`Detected ${contours.length} contour(s) after morphology`);

  // Step 6: Build crowd regions
  const crowdRegions = buildCrowdRegions(
    contours,
    gatherings,
    weaponPositions,
    thresholds
  );

  // Step 7: Determine flags
  const weaponGlobal = weaponPositions.length > 0;
  const crowd = crowdRegions.some((r) => r.insideCount >= thresholds.crowdThreshold);
  const mob = crowdRegions.some((r) => r.mob);

  if (weaponGlobal) {
    reasoning.push(`⚠ Weapon detected globally (${weaponPositions.length} weapon(s))`);
  }

  for (const r of crowdRegions) {
    if (r.insideCount >= thresholds.crowdThreshold) {
      reasoning.push(`🔶 CROWD in region ${r.id}: ${r.insideCount} people (threshold: ${thresholds.crowdThreshold})`);
      if (r.mob) {
        reasoning.push(`🔴 MOB ALERT in region ${r.id}: crowd + weapon presence`);
      }
    }
  }

  if (!crowd && !mob) {
    reasoning.push('✅ No crowd or mob detected');
  }

  return {
    gatherings,
    heatmapGrid,
    binaryMask: closedMask,
    contours,
    crowdRegions,
    flags: { crowd, mob, weaponGlobal },
    reasoning,
  };
}
